import { useEffect, useState } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useApp } from '@/components/AppLayout';
import { taxApi, type CreateTaxData, type TaxTypeChoice } from '@/lib/api';
import { ArrowLeft, Plus } from 'lucide-react';

function AddTaxContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();
  const [taxTypeChoices, setTaxTypeChoices] = useState<TaxTypeChoice[]>([]);

  const [name, setName] = useState('');
  const [rate, setRate] = useState('');
  const [taxType, setTaxType] = useState('exclusive');
  const [applyToAllParts, setApplyToAllParts] = useState(false);
  const [applyToAllLabor, setApplyToAllLabor] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    taxApi.getTaxTypes().then((response) => {
      if (response.data) {
        setTaxTypeChoices(response.data);
      }
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentBusiness) return;

    setError('');
    setSubmitting(true);

    const data: CreateTaxData = {
      name,
      rate,
      tax_type: taxType,
      apply_to_all_parts: applyToAllParts,
      apply_to_all_labor: applyToAllLabor,
      business: currentBusiness.id,
    };

    const response = await taxApi.create(data);

    setSubmitting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    nav('/taxes');
  };

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav('/taxes')}
          className="gap-2 text-muted-foreground hover:text-foreground -ml-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tax Rates
        </Button>
      </div>

      <div className="space-y-1 mb-8">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Plus className="h-6 w-6" />
          Add New Tax
        </h1>
        <p className="text-sm text-muted-foreground">
          Create a new tax rate for your invoices and quotes.
        </p>
      </div>

      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Tax Name *</Label>
              <Input
                id="name"
                placeholder="e.g., Sales Tax, VAT, GST"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rate">Tax Rate (%) *</Label>
              <Input
                id="rate"
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="e.g., 8.25"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                required
              />
              <p className="text-xs text-muted-foreground">Enter the percentage rate (e.g., 8.25 for 8.25%)</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="taxType">Tax Type</Label>
              <Select value={taxType} onValueChange={setTaxType}>
                <SelectTrigger>
                  <SelectValue placeholder="Select tax type" />
                </SelectTrigger>
                <SelectContent>
                  {taxTypeChoices.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                <strong>Exclusive:</strong> Tax added on top of item price<br />
                <strong>Inclusive:</strong> Tax included in item price
              </p>
            </div>

            <div className="space-y-3">
              <Label>Auto-apply this tax</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applyToAllParts"
                    checked={applyToAllParts}
                    onCheckedChange={(checked) => setApplyToAllParts(checked === true)}
                  />
                  <Label htmlFor="applyToAllParts" className="font-normal cursor-pointer">
                    Apply to all parts/products by default
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="applyToAllLabor"
                    checked={applyToAllLabor}
                    onCheckedChange={(checked) => setApplyToAllLabor(checked === true)}
                  />
                  <Label htmlFor="applyToAllLabor" className="font-normal cursor-pointer">
                    Apply to all labor by default
                  </Label>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                When enabled, this tax will be automatically applied to new items of the selected type
              </p>
            </div>

            {error && (
              <p className="text-sm text-destructive">{error}</p>
            )}

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => nav('/taxes')} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={submitting || !name || !rate}>
                {submitting ? 'Creating...' : 'Create Tax'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export function AddTax() {
  return <AddTaxContent />;
}
